import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Send } from 'lucide-react';

interface AIWriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNotificationGenerated: (title: string, message: string) => void;
  category: string;
  priority: string;
}

export const AIWriteModal: React.FC<AIWriteModalProps> = ({
  isOpen,
  onClose,
  onNotificationGenerated,
  category,
  priority
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();

  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleClose = () => {
    setPrompt('');
    onClose();
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: t('aiWrite.error.emptyPrompt', 'Empty Prompt'),
        description: t('aiWrite.error.emptyPromptDesc', 'Please provide a description of what you want to communicate.'),
        variant: 'destructive',
      });
      return;
    }

    if (!user) {
      toast({
        title: t('aiWrite.error.auth', 'Authentication Error'),
        description: t('aiWrite.error.authDesc', 'You must be logged in to use AI writing assistance.'),
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-notification-message', {
        body: {
          prompt: prompt.trim(),
          category: category,
          priority: priority,
          context: `This is a ${priority} priority ${category} notification for a business platform.`
        }
      });

      if (error) {
        throw error;
      }

      if (data.success && data.title && data.message) {
        onNotificationGenerated(data.title, data.message);
        toast({
          title: t('aiWrite.success.title', 'Notification Generated'),
          description: t('aiWrite.success.desc', 'AI has generated your notification title and message successfully.'),
        });
        handleClose();
      } else {
        throw new Error('No title or message generated');
      }

    } catch (error: any) {
      console.error('Error generating message:', error);
      toast({
        title: t('aiWrite.error.generate', 'Generation Failed'),
        description: error.message || t('aiWrite.error.generateDesc', 'Failed to generate message. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getPlaceholderText = () => {
    switch (category) {
      case 'maintenance':
        return t('aiWrite.placeholder.maintenance', 'e.g., "We\'re upgrading servers tonight 2-4 AM EST for better performance"');
      case 'security':
        return t('aiWrite.placeholder.security', 'e.g., "Please update your password to keep your account secure"');
      case 'feature':
        return t('aiWrite.placeholder.feature', 'e.g., "Check out our new AI matching feature - it\'s now live!"');
      case 'billing':
        return t('aiWrite.placeholder.billing', 'e.g., "We\'ve updated your billing details with clearer breakdowns"');
      case 'policy':
        return t('aiWrite.placeholder.policy', 'e.g., "Our privacy policy has been updated - please review the changes"');
      default:
        return t('aiWrite.placeholder.general', 'e.g., "We have an important platform update to share"');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            {t('aiWrite.title', 'Write with AI')}
          </DialogTitle>
          <DialogDescription>
            {t('aiWrite.description', 'Describe what you want to communicate and AI will generate both a title and message for your notification.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="ai-prompt">
              {t('aiWrite.label.prompt', 'What do you want to communicate?')} *
            </Label>
            <Textarea
              id="ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={getPlaceholderText()}
              rows={4}
              maxLength={1000}
              disabled={isGenerating}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {prompt.length}/1000 {t('aiWrite.characters', 'characters')}
            </p>
          </div>

          <div className="bg-muted/30 p-3 rounded-sm space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              {t('aiWrite.context', 'Context:')}
            </p>
            <div className="flex gap-4 text-xs">
              <span>
                <strong>{t('aiWrite.category', 'Category:')}</strong> {category}
              </span>
              <span>
                <strong>{t('aiWrite.priority', 'Priority:')}</strong> {priority}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isGenerating}
          >
            {t('aiWrite.button.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('aiWrite.button.generating', 'Generating...')}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {t('aiWrite.button.generate', 'Generate Message')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
